import React, {useState, useRef, useEffect} from "react";
import GoogleMapReact from "google-map-react";
import useSupercluster from "use-supercluster";
import './google-map-cluster.scss';
import {AppVersionForTenants, Config} from "../../../constants/Config";
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from "../../../store/configureStore";
import {clubFinderSearchBarSliceActions} from '../../../store/slices/clubFinderSearchBarSlice';

interface PropTypes {
  rawDataPoints: any
  defaultCenter?: {
    lat: number,
    lng: number
  }
  defaultZoom?: number
  updatedZoom?: number
  newCenter?: {
    lat: number,
    lng: number
  }
  mapMarkerClickAction: any
  singleResultMarker: boolean
  userLocation: {
    userLat: number | null
    userLng: number | null
  }
}

type MarkerProps = {
  children: JSX.Element
  lat: number
  lng: number
}

type userLocationAddressPropType = {
  route?: string,
  suburb?: string,
  state?: string
}


const Marker = ({children}: MarkerProps): JSX.Element => children;
const baseClass = 'clubFinder-google-map-cluster';

export default function GoogleMapCluster(props: PropTypes) {
  const dispatch = useDispatch();
  const mapRef = useRef();
  const mapsRef = useRef();
  const selectedLocation = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedLocation);
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(10);
  const [googleMapApiKey, setGoogleMapApiKey] = useState('');
  const googleMapConfig = useSelector((state: RootState) => state.widgetConfigurationSettings.googleMapConfig);
  const tenant = useSelector((state: RootState) => state.widgetConfigurationSettings.tenant);
  const toggleUserLocationFunction = useSelector((state: RootState) => state.clubFinderMapResultStore.toggleUserLocationFunction);
  const [componentZoom, setComponentZoom] = useState(0);
  const [componentCenter, setComponentCenter] = useState(Config.map.defaultCenter[tenant]);

  useEffect(() => {
    if (googleMapConfig.mapsAPIKey && googleMapConfig.mapsAPIKey.length) {
      setGoogleMapApiKey(googleMapConfig.mapsAPIKey);
    }
  }, [googleMapConfig.mapsAPIKey]);

  const points = props.rawDataPoints.map((rawDataPoints: any, index: number) => ({
    type: "Feature",
    properties: {
      cluster: false,
      dataPointId: `${rawDataPoints.orgName.replace(' ', '_')}_${index}`,
      orgId: rawDataPoints.orgId,
      distance: rawDataPoints.distance ? rawDataPoints.distance : '',
    },
    geometry: {
      type: "Point",
      coordinates: [
        parseFloat(rawDataPoints.address.longitude),
        parseFloat(rawDataPoints.address.latitude)
      ]
    }
  }));

  const {clusters, supercluster} = useSupercluster({
    points,
    bounds,
    zoom,
    options: {radius: 50, maxZoom: Config.map.maxZoom}
  });

  useEffect(() => {
    if (props.userLocation.userLat && props.userLocation.userLng && AppVersionForTenants.v2.tenants.includes(tenant)) {
      reverseGeocoding(props.userLocation.userLat, props.userLocation.userLng)
    }
  }, [props.userLocation.userLng, props.userLocation.userLat, mapsRef, tenant])

  useEffect(() => {
    if (AppVersionForTenants.v2.tenants.includes(tenant)) {
      if (props.userLocation.userLat && props.userLocation.userLng) {
        reverseGeocoding(props.userLocation.userLat, props.userLocation.userLng)
      }
    }
  }, [toggleUserLocationFunction, tenant])

  const reverseGeocoding = (lat: number | null, lng: number | null) => {
    if (mapsRef && mapsRef.current && lat && lng) {
      // @ts-ignore
      const geocoder = new mapsRef.current.Geocoder();
      geocoder.geocode({location: {lat, lng}}, (results: any, status: any) => {
        dispatch(clubFinderSearchBarSliceActions.setSelectedLocation({address: {latitude: lat, longitude: lng}}));
        if (status === 'OK' && results[0]) {
          const address = results[0]
          const userLocationAddress: userLocationAddressPropType = {};

          address.address_components.forEach((component: any) => {
            const types = component.types;
            if (types.includes("route")) {
              userLocationAddress.route = component.short_name;
            }
            if (types.includes("locality")) {
              userLocationAddress.suburb = component.long_name;
            }
            if (types.includes("administrative_area_level_1")) {
              userLocationAddress.state = component.short_name;
            }
          });

          if (userLocationAddress) {
            dispatch(clubFinderSearchBarSliceActions.setUserLocationAddress(userLocationAddress))
            dispatch(clubFinderSearchBarSliceActions.toggleMobileSearch(false));
            dispatch(clubFinderSearchBarSliceActions.searchButtonPressed(true));
          }
        } else {
          console.log('Address not found');
        }
      });
    }
  }

  useEffect(() => {
    // at zoom level 5 for AFL and NA, on the large screens (e.g. 3000px wide), the map only
    // loads on half a screen. Zooming the map again renders the full map.
    // This should execute only when there is no location/user-location selected.
      const timer = setTimeout(() => {
        if(AppVersionForTenants.v2.tenants.includes(tenant)) {
          if ((props.userLocation.userLat && props.userLocation.userLng) || selectedLocation != null) {
            setComponentZoom(Config.map.clusterZoom[tenant]);
          } else {
            setComponentZoom(Config.map.controlledAreaZoom[tenant]);
          }
        }
      },1500);

    return () => {
      clearTimeout(timer);
    }

  }, [tenant, selectedLocation]);

  useEffect(() => {
    if (props.updatedZoom != undefined) {
      setComponentZoom(props.updatedZoom);
    }
    // Added a timer because the map will not recenter sometimes (probably) when there is a delay in loading the map
    const timer = setTimeout(() => {
      // @ts-ignore
      setComponentCenter(props.newCenter);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [props.newCenter, props.updatedZoom]);

  return (
    <div className={baseClass} style={{height: "100%", width: "100%"}}>
      {googleMapApiKey && googleMapApiKey.length > 0 && (

        <GoogleMapReact
          bootstrapURLKeys={{key: googleMapApiKey}}
          // @ts-ignore
          defaultCenter={Config.map.defaultCenter[tenant]}
          // @ts-ignore
          defaultZoom={props.defaultZoom || Config.map.defaultZoom[tenant]}
          options={{mapId: googleMapConfig.mapId ? googleMapConfig.mapId : ''}}
          center={componentCenter}
          zoom={componentZoom}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({map, maps}) => {
            mapRef.current = map;
            mapsRef.current = maps;
            if (AppVersionForTenants.v2.tenants.includes(tenant)) {
              reverseGeocoding(props.userLocation.userLat, props.userLocation.userLng)
            }
          }}
          onChange={({zoom, bounds}) => {
            setZoom(zoom);
            // @ts-ignore
            setBounds([
              bounds.nw.lng,
              bounds.se.lat,
              bounds.se.lng,
              bounds.nw.lat
            ]);
          }}
          onDragEnd={(map) => {
            if (map.zoom >= Config.map.minDragAndSearchZoom && !props.singleResultMarker) {
              dispatch(clubFinderSearchBarSliceActions.setSelectedLocation({
                address: {
                  latitude: map.center.lat(),
                  longitude: map.center.lng()
                }
              }));
            }
          }}
        >
          {clusters.map(cluster => {
            const [longitude, latitude] = cluster.geometry.coordinates;
            const {
              cluster: isCluster,
              point_count: pointCount
            } = cluster.properties;
            if (isCluster) {
              return (
                <Marker
                  key={`cluster-${cluster.id}`}
                  // @ts-ignore
                  lat={latitude}
                  lng={longitude}
                >
                  <div
                    className={`${baseClass}--clusterMarker`}
                    style={{
                      width: `${16 + (pointCount / points.length) * 30}px`,
                      height: `${16 + (pointCount / points.length) * 30}px`,
                      fontSize: `${16 + (pointCount / points.length) * 4}px`
                    }}
                    onClick={() => {
                      const expansionZoom = Math.min(
                        supercluster.getClusterExpansionZoom(cluster.id),
                        20
                      );
                      // @ts-ignore
                      mapRef.current.setZoom(expansionZoom);
                      // @ts-ignore
                      mapRef.current.panTo({lat: latitude, lng: longitude});
                    }}
                  >
                    {pointCount}
                  </div>
                </Marker>
              );
            }

            return (
              <Marker
                key={`dataPoint-${cluster.properties.dataPointId}`}
                lat={latitude}
                lng={longitude}
              >
                <button
                  className={`${baseClass}--buttonMarker ${props.singleResultMarker ? baseClass + '--buttonMarker-single' : ''}`}
                  onClick={() => props.mapMarkerClickAction(cluster.properties.orgId, cluster.properties.distance)}>
                  <div
                    className={`${baseClass}--buttonMarker-icon-container ${props.singleResultMarker ? baseClass + '--buttonMarker-single-icon-container' : ''}`}>
                    <img src={Config.map.mapMarkerIcons[tenant]} alt="Marker"/>
                  </div>
                </button>
              </Marker>
            );
          })}
        </GoogleMapReact>

      )}
    </div>
  );
}
